// ── Email HTML Builder ────────────────────────────────────

import { ensureBlockBreaks } from './text.js'

const SUPABASE_ASSETS = 'https://muqmgxvdkwmhuplrulcc.supabase.co/storage/v1/object/public/assets'
const LOGO_URL = `${SUPABASE_ASSETS}/Logo.gif`
const WINDSOR_URL = `${SUPABASE_ASSETS}/WindsorExtraBoldCondensed.woff2`
const FEEDBACK_URL = 'https://forms.gle/LB1DJKnj3V2ikEdC7'

const SLUG_STYLE = `font-family: 'Courier New', Courier, monospace; font-weight: bold; color: #5E80FB; font-size: 0.8em; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 0.1em; margin-top: 2.5em; display: block;`
const DATE_STYLE = `font-family: 'Courier New', Courier, monospace; font-weight: bold; color: #767676; font-size: 0.72em; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 10px; margin-top: 2em; display: block; text-align: center;`

const SPORTS = new Set([
  'CLIMBING', 'MOUNTAINEERING', 'TRAIL RUNNING', 'MOUNTAIN RUNNING',
  'BACKCOUNTRY SKIING', 'SKI TOURING', 'PARAGLIDING', 'PACKRAFTING',
  'MOUNTAIN BIKING', 'MULTI-SPORT', 'ROCK CLIMBING', 'ICE CLIMBING',
  'ALPINISM', 'RUNNING', 'SKIING', 'ADVENTURE', 'GEAR', 'NOTABLE ASCENTS'
])

const DAY_PATTERN = /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),\s+\w+ \d+(,\s+\d{4})?$/

function isSlug(text) {
  const clean = text.trim().replace(/`/g, '').trim()
  const words = clean.split(/\s+/)
  return (clean === clean.toUpperCase() && words.length >= 1 && words.length <= 5) || SPORTS.has(clean)
}

function applyInline(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #5E80FB;">$1</a>')
}

function renderParagraphs(digestText) {
  const parts = []
  // Normalize single newlines within paragraphs — but not between list items
  const normalised = ensureBlockBreaks(digestText).replace(/([^\n-])\n([^\n-])/g, '$1 $2')
  const paragraphs = normalised.split(/\n\n+/)
  let firstRuleSeen = false

  for (const raw of paragraphs) {
    const p = raw.trim()
    if (!p) continue

    if (p.startsWith('STORY_IMAGE: ')) {
      const rest = p.slice('STORY_IMAGE: '.length)
      const pipeIdx = rest.indexOf(' | ')
      const url = pipeIdx >= 0 ? rest.slice(0, pipeIdx).trim() : rest.trim()
      const meta = pipeIdx >= 0 ? rest.slice(pipeIdx + 3) : ''
      const dblPipe = meta.indexOf(' || ')
      const caption = dblPipe >= 0 ? meta.slice(0, dblPipe).trim() : meta.trim()
      const credit = dblPipe >= 0 ? meta.slice(dblPipe + 4).trim() : ''
      let creditLine = ''
      if (caption && credit) creditLine = caption + ' / <em>' + credit + '</em>'
      else if (caption) creditLine = caption
      else if (credit) creditLine = '<em>' + credit + '</em>'
      const captionHtml = creditLine
        ? `<tr><td style="padding:0.3em 0 0 0;font-size:0.72em;color:#767676;font-family:'Courier New',monospace;">${creditLine}</td></tr>`
        : ''
      // 10px space above images
      parts.push(
        `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:10px 0 1.5em 0;">` +
        `<tr><td style="padding:0;"><img src="${url}" alt="" width="100%" style="width:100%!important;max-width:100%!important;height:auto;display:block;border:0;"></td></tr>` +
        captionHtml +
        `</table>`
      )
    } else if (p === '---') {
      // First rule gets 10px below, subsequent rules are normal
      const marginBottom = !firstRuleSeen ? '10px' : '0'
      firstRuleSeen = true
      parts.push(`<hr style="border: none; border-top: 1px solid #5E80FB; margin: 2.4em 0 ${marginBottom} 0;">`)
    } else if (DAY_PATTERN.test(p)) {
      parts.push(`<p style="${DATE_STYLE}">${p}</p>`)
    } else if (p.startsWith('## ')) {
      // Windsor headers — letter-spacing: 0em
      parts.push(`<h2 style="font-family: Windsor, Georgia, serif; font-weight: bold; font-size: 1.75em; line-height: 1.0; margin-top: 0.1em; margin-bottom: 0.4em; color: #1a1a1a; letter-spacing: 0em;">${applyInline(p.slice(3))}</h2>`)
    } else if (p.startsWith('# ')) {
      parts.push(`<h1 style="font-family: Windsor, Georgia, serif; font-weight: bold; font-size: 2.1em; line-height: 1.1; color: #1a1a1a; letter-spacing: 0em;">${applyInline(p.slice(2))}</h1>`)
    } else if (p.startsWith('- ') || p.includes('\n- ')) {
      const items = p.split('\n').filter(l => l.trim().startsWith('- '))
      const liHtml = items.map(item => `<li style="margin-bottom: 0.4em;">${applyInline(item.replace(/^-\s+/, ''))}</li>`).join('')
      parts.push(`<ul style="padding-left: 1.3em; margin: 0.8em 0;">${liHtml}</ul>`)
    } else if (isSlug(p)) {
      parts.push(`<p style="${SLUG_STYLE}">${p.replace(/`/g, '').trim()}</p>`)
    } else if (p.startsWith('*') && p.endsWith('*') && !p.startsWith('**')) {
      // Italic intro paragraph — 10px space above
      parts.push(`<p style="font-style: italic; color: #444; font-size: 1em; line-height: 1.49; margin-top: 10px; margin-bottom: 1em;">${applyInline(p.slice(1, -1))}</p>`)
    } else {
      parts.push(`<p style="margin-bottom: 1em; color: #1a1a1a;">${applyInline(p)}</p>`)
    }
  }
  return parts.join('\n')
}

export function buildEmailHtml(digestText, subscribeUrl) {
  const bodyHtml = renderParagraphs(digestText)
  const subUrl = subscribeUrl || (typeof window !== 'undefined' ? window.location.origin + '/subscribe' : '/subscribe')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <style>
    @font-face {
      font-family: 'Windsor';
      src: url('${WINDSOR_URL}') format('woff2');
      font-weight: bold;
      font-style: normal;
    }
    :root { color-scheme: light only; }
    body { background-color: #FBFAFA !important; color: #1a1a1a !important; }
    @media (prefers-color-scheme: dark) {
      body { background-color: #FBFAFA !important; color: #1a1a1a !important; }
      * { background-color: inherit; color: inherit; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #FBFAFA; color: #1a1a1a;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #FBFAFA;">
    <tr>
      <td align="center" style="padding: 0;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 600px; background-color: #FBFAFA;">

          <!-- Logo -->
          <tr>
            <td align="center" style="padding: 18px 12px 0 12px;">
              <img src="${LOGO_URL}" alt="Inside Magazine" width="400"
                style="display: block; width: 100%; max-width: 400px; height: auto; border: 0;">
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding: 0 12px 16px 12px; font-family: Georgia, serif; font-size: 17.6px; line-height: 1.49; color: #1a1a1a;">
              ${bodyHtml}
            </td>
          </tr>

          <!-- Subscribe / Unsubscribe / Feedback footer -->
          <tr>
            <td align="center" style="padding: 30px 12px 32px 12px;">
              <p style="font-family: 'Courier New', Courier, monospace; font-size: 0.85em;
                color: #767676; margin: 0;">
                Not already subscribed? <a href="${subUrl}"
                  style="color: #767676; text-decoration: underline;">Click here</a>
                &nbsp;|&nbsp;
                <a href="{{UNSUBSCRIBE_URL}}"
                  style="color: #767676; text-decoration: underline;">Unsubscribe</a>
                &nbsp;|&nbsp;
                <a href="${FEEDBACK_URL}"
                  style="color: #767676; text-decoration: underline;">Feedback</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}
